import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import LoadingScreen from "../Utils/LoadingScreen";

import {
  ClipboardList,
  CheckCircle,
  XCircle,
  IndianRupee,
  Wallet,
  FileDown,
  ThumbsUp,
  ThumbsDown,
} from "lucide-react";

import { exportAnyJsonToExcel } from "../Utils/exportAnyJsonToExcel";
import useSettlement from "../../Sevices/useSettlement";

const fmt2 = (num) =>
  Number(num || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const SettlementDetail = () => {
  const { driverId, username } = useParams();
  const navigate = useNavigate();

  const {
    fetchDriverAnalytics,
    fetchSettlementReport,
    handleSettleAmount,
    loading,
    settlementLoading,
    paying,
  } = useSettlement();

  const today = new Date().toISOString().split("T")[0];

  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);

  const [analytics, setAnalytics] = useState(null);
  const [settlementRows, setSettlementRows] = useState([]);

  const [amountRows, setAmountRows] = useState([]);

  const [payAmount, setPayAmount] = useState(0);
  const [bonusAmount, setBonusAmount] = useState(0);
  const [showConfirm, setShowConfirm] = useState(false);
  const [amountPaid, setAmountPaid] = useState(null);

  /*  INITIAL LOAD */
  useEffect(() => {
    fetchDriverAnalytics(driverId, fromDate, toDate, setAnalytics);
    fetchSettlementReport(driverId, fromDate, toDate, setSettlementRows);
  }, [driverId]);

  /*  APPLY FILTER  */
  const handleApply = () => {
    fetchDriverAnalytics(driverId, fromDate, toDate, setAnalytics);
    fetchSettlementReport(driverId, fromDate, toDate, setSettlementRows);
  };

  /*  EXPORT  */
  const handleExport = () => {
    if (!settlementRows.length) {
      alert("No settlement data available");
      return;
    }

    exportAnyJsonToExcel({
      data: settlementRows,
      filePrefix: `Settlement_${driverId}_${fromDate}_to_${toDate}`,
      sheetName: "Settlement",
    });
  };

  const showValue = (val, isAmount = false) => {
    if (val === null || val === undefined) return "N/A";
    return isAmount ? `₹ ${fmt2(val)}` : val;
  };

  const amountMap = amountRows.reduce((acc, row) => {
    acc[row.id] = row;
    return acc;
  }, {});

  const totalAmount = amountRows.reduce(
    (sum, row) => sum + Number(row.amount || 0),
    0,
  );

  const totalExtra = amountRows.reduce(
    (sum, row) => sum + Number(row.extra || 0),
    0,
  );

  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen space-y-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-end gap-4">
          <div>
            <h2 className="text-lg md:text-xl font-semibold">
              Packky Driver Settlement — {decodeURIComponent(username || "")}
              <span className="text-gray-400 font-normal"> ({driverId})</span>
            </h2>
            <p className="text-sm text-gray-500">
              Filter, analyze & export settlement data
            </p>
          </div>

          <div className="flex gap-3 items-end">
            <div>
              <label className="block text-xs mb-1">From</label>
              <input
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="border p-2 rounded text-sm"
              />
            </div>

            <div>
              <label className="block text-xs mb-1">To</label>
              <input
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="border p-2 rounded text-sm"
              />
            </div>

            <button
              onClick={handleApply}
              className="h-10 px-4 bg-blue-600 text-white rounded text-sm"
            >
              Apply
            </button>

            <button
              onClick={handleExport}
              className="h-10 px-4 bg-green-600 text-white rounded text-sm flex items-center gap-2"
            >
              <FileDown size={16} /> Export
            </button>

            <button
              onClick={() => navigate(-1)}
              className="h-10 px-4 border rounded text-sm"
            >
              ← Back
            </button>
          </div>
        </div>

        {/*  ANALYTICS  */}
        <h3 className="text-xl font-semibold">Driver Settlement</h3>

        {loading && <LoadingScreen />}

        {!loading && analytics && (
          <div className="grid gap-3 md:grid-cols-5">
            <StatCard
              icon={<ClipboardList />}
              label="Assigned Orders"
              value={showValue(analytics.assigned_orders)}
            />

            <StatCard
              icon={<CheckCircle />}
              label="Successful Orders"
              value={showValue(analytics.current_successful_orders)}
              color="green"
            />

            <StatCard
              icon={<XCircle />}
              label="Declined Orders"
              value={showValue(analytics.declined_orders)}
              color="red"
            />

            <StatCard
              icon={<IndianRupee />}
              label="Total Amount"
              value={showValue(fmt2(totalAmount), true)}
            />

            {/* <StatCard
      icon={<Wallet />}
      label="Total Extra"
      value={showValue(fmt2(totalExtra), true)}
    /> */}

            <StatCard
              icon={<IndianRupee />}
              label="Amount Paid"
              value={amountPaid !== null ? `₹ ${fmt2(amountPaid)}` : "N/A"}
              color="green"
            />
          </div>
        )}

        {/* ADD AMOUNT  */}
        <div className="bg-white border rounded-xl p-4 flex flex-wrap gap-4 items-end">
          <div>
            <label className="block text-xs mb-1">Pay Amount (₹)</label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={payAmount}
              onChange={(e) => setPayAmount(e.target.value)}
              onWheel={(e) => e.target.blur()}
              className="border p-2 rounded w-40 appearance-none"
              placeholder="0"
            />
          </div>

          <div>
            <label className="block text-xs mb-1">Bonus Amount (₹)</label>
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              value={bonusAmount}
              onChange={(e) => setBonusAmount(e.target.value)}
              onWheel={(e) => e.target.blur()}
              className="border p-2 rounded w-40 appearance-none"
              placeholder="0"
            />
          </div>

          <button
            onClick={() => setShowConfirm(true)}
            disabled={paying}
            className="bg-orange-500 text-white px-6 py-2 rounded"
          >
            {paying ? "Paying..." : "Settle Amount"}
          </button>
        </div>

        {/*  SETTLEMENT TABLE  */}
        <div className="overflow-x-auto border rounded-md shadow-sm bg-white">
          <table className="min-w-full">
            <thead>
              <tr className="bg-orange-400 text-white text-sm">
                <th className="px-4 py-2 text-center">S/N</th>
                <th className="px-4 py-2 text-center">Task ID</th>
                <th className="px-4 py-2 text-center">Task Description</th>
                <th className="px-4 py-2 text-center">Payment Mode</th>
                <th className="px-4 py-2 text-center">Task Status</th>
                <th className="px-4 py-2 text-center">Settlement Status</th>
              </tr>
            </thead>

            <tbody>
              {settlementLoading ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-500">
                    <LoadingScreen />
                  </td>
                </tr>
              ) : settlementRows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-6 text-center text-gray-400">
                    No settlement records found
                  </td>
                </tr>
              ) : (
                settlementRows.map((row, index) => {
                  const amountInfo = amountMap[row.task_id] || {};

                  if (loading && !analytics) {
                    return <LoadingScreen />;
                  }

                  return (
                    <tr key={row.task_id} className="border-t text-sm">
                      <td className="px-4 py-2 text-center">{index + 1}</td>
                      <td className="px-4 py-2 text-center">{row.task_id}</td>
                      <td className="px-4 py-2 text-center">
                        {row.task_description || "N/A"}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {row.payment_mode || "N/A"}
                      </td>

                      <td className="px-4 py-2 text-center capitalize">
                        {row.task_status}
                      </td>
                      <td className="px-4 py-2 text-center">
                        {row.settlement_status?.toUpperCase() === "Y" ? (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl text-xs font-medium bg-green-100 text-green-700">
                            <ThumbsUp size={14} />
                            Settled
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-xl text-xs font-medium bg-red-100 text-red-700">
                            <ThumbsDown size={14} />
                            Unsettled
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-lg w-full max-w-md p-6 space-y-4">
            {/* Header */}
            <h3 className="text-lg font-semibold text-gray-800">
              Confirm Settlement
            </h3>

            {/* Message */}
            <p className="text-sm text-gray-600">
              Are you sure you want to settle amount for:
            </p>

            <div className="bg-gray-50 border rounded-lg p-3 text-sm space-y-1">
              <p>
                <span className="font-medium">Driver:</span>{" "}
                {decodeURIComponent(username || "")} ({driverId})
              </p>
              <p>
                <span className="font-medium">From:</span> {fromDate}
              </p>
              <p>
                <span className="font-medium">To:</span> {toDate}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 border rounded text-sm"
              >
                Cancel
              </button>

              <button
                onClick={async () => {
                  await handleSettleAmount(
                    driverId,
                    fromDate,
                    toDate,
                    payAmount,
                    bonusAmount,
                    setAmountPaid,
                    () =>
                      fetchDriverAnalytics(
                        driverId,
                        fromDate,
                        toDate,
                        setAnalytics,
                      ),
                    () =>
                      fetchSettlementReport(
                        driverId,
                        fromDate,
                        toDate,
                        setSettlementRows,
                      ),
                  );
                  setShowConfirm(false);
                }}
                className="px-4 py-2 bg-orange-500 text-white rounded text-sm"
              >
                Confirm & Settle
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/*  SMALL STAT CARD  */
const StatCard = ({ icon, label, value, color = "blue" }) => (
  <div
    className={`rounded-2xl border border-l-4 border-l-${color}-500 bg-white p-4`}
  >
    <div className="flex items-center gap-3">
      <div className={`text-${color}-600`}>{icon}</div>
      <div>
        <div className="text-xs text-gray-500">{label}</div>
        <div className="text-lg font-semibold">{value}</div>
      </div>
    </div>
  </div>
);

export default SettlementDetail;
